import { StarIcon } from '@heroicons/react/20/solid';
import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { NavLink } from 'react-router-dom';

function formatPrice(value) {
  const price = Number(value) || 0;
  return price.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

export default function ProductCard({ product, index = 0 }) {
  const {
    id,
    title,
    thumbnail,
    price,
    discountPercentage = 0,
    rating = 0,
    category,
    stock = 0,
  } = product;

  const originalPrice =
    discountPercentage > 0 ? price / (1 - discountPercentage / 100) : null;

  return (
    <NavLink
      to={`/detail/${id}`}
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
      className="group relative flex animate-fade-up flex-col overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-soft transition duration-300 hover:-translate-y-1.5 hover:border-brand-200 hover:shadow-lift"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <img
          src={thumbnail}
          alt={title}
          loading="lazy"
          className="h-full w-full object-cover transition duration-500 ease-out group-hover:scale-110"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/25 via-transparent to-transparent opacity-0 transition duration-300 group-hover:opacity-100" />

        <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold capitalize text-slate-700 shadow-sm backdrop-blur">
          {String(category || 'product').replaceAll('-', ' ')}
        </span>

        {discountPercentage > 0 && (
          <span className="absolute right-3 top-3 rounded-full bg-rose-500 px-2.5 py-1 text-[11px] font-bold text-white shadow-sm">
            -{Math.round(discountPercentage)}%
          </span>
        )}

        {stock === 0 && (
          <span className="absolute inset-x-3 bottom-3 rounded-lg bg-slate-900/80 py-1.5 text-center text-xs font-semibold text-white backdrop-blur">
            Stok habis
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold leading-5 text-slate-800 transition group-hover:text-brand-700">
          {title}
        </h3>

        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <span className="flex items-center gap-0.5 text-amber-400">
            <StarIcon className="h-3.5 w-3.5" />
            <span className="font-semibold text-slate-700">
              {Number(rating).toFixed(1)}
            </span>
          </span>
          <span className="text-slate-300">•</span>
          <span>{stock > 0 ? `${stock} tersedia` : 'stok habis'}</span>
        </div>

        <div className="mt-auto flex items-end justify-between pt-2">
          <div className="flex flex-col">
            <span className="text-lg font-bold text-slate-900">
              {formatPrice(price)}
            </span>
            {originalPrice && (
              <span className="text-xs text-slate-400 line-through">
                {formatPrice(originalPrice)}
              </span>
            )}
          </div>

          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition duration-300 group-hover:bg-brand-600 group-hover:text-white">
            <ArrowUpRightIcon className="h-4 w-4" strokeWidth={2.5} />
          </span>
        </div>
      </div>
    </NavLink>
  );
}
